import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react"
import { tokenManager } from "@/lib/token-manager"

type AuthContextType = {
  token: string | null
  setToken: (token: string | null) => void
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

function readTokenFromUrl(): string | null {
  if (typeof window === "undefined") return null
  const url = new URL(window.location.href)
  const token = url.searchParams.get("token")
  if (!token) return null

  url.searchParams.delete("token")
  const nextUrl = `${url.pathname}${url.search}${url.hash}`
  window.history.replaceState(null, "", nextUrl)

  return token
}

function getTrustedOrigins(): Set<string> {
  const origins = new Set<string>()
  // Self origin: allow top-level/local message flow from this app origin.
  if (typeof window !== "undefined" && typeof window.location.origin === "string") {
    origins.add(window.location.origin)
  }

  // Parent origin: when embedded, derive the direct parent site from referrer.
  if (typeof document !== "undefined" && typeof document.referrer === "string" && document.referrer) {
    try {
      origins.add(new URL(document.referrer).origin)
    } catch {
      // Ignore malformed referrer values.
    }
  }

  return origins
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Lazy initializer runs synchronously during render — before any useEffect fires —
  // so fetchWithAuth can read the token immediately even if a child effect runs first.
  const [token, setToken] = useState<string | null>(() => {
    const t = readTokenFromUrl()
    if (t) tokenManager.notifyTokenUpdate(t)
    return t
  })
  const hasInitialToken = useRef(token !== null)

  useEffect(() => {
    if (typeof window === "undefined") return

    if (hasInitialToken.current) {
      // Token already synced to tokenManager in the useState initializer above.
    } else {
      // No URL token — ask the embedding parent frame for one.
      // The parent responds with a SET_TOKEN message via event.source so the window
      // reference is always correct — no stored refs or polling needed.
      window.parent.postMessage({ action: "REQUEST_TOKEN" }, "*")
    }

    const trustedOrigins = getTrustedOrigins()

    const handleMessage = (event: MessageEvent) => {
      // Only accept token messages from the direct embedding parent frame.
      // In top-level (non-iframe) mode, parent===self and synthetic test events may have null source.
      const isDirectParentSource =
        event.source === window.parent || (window.parent === window && event.source === null)
      if (!isDirectParentSource) {
        return
      }

      if (typeof event.origin !== "string" || !trustedOrigins.has(event.origin)) {
        return
      }

      const data = event.data
      if (data && data.action === "SET_TOKEN" && typeof data.token === "string") {
        setToken(data.token)
        tokenManager.notifyTokenUpdate(data.token)
      }
    }
    window.addEventListener("message", handleMessage)
    return () => window.removeEventListener("message", handleMessage)
  }, [])

  return (
    <AuthContext.Provider
      value={{
        token,
        setToken,
        isAuthenticated: !!token,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
